import { useCurrentStateAndParams } from '@uirouter/react';
import { ReactNode } from 'react';

import { useEnvironmentId } from '@/react/hooks/useEnvironmentId';
import { EnvironmentId } from '@/react/portainer/environments/types';
import { useHelmRelease } from '@/react/kubernetes/helm/helmReleaseQueries/useHelmRelease';
import { GenericResource } from '@/react/kubernetes/helm/types';

import { Datatable, TableSettingsMenu } from '@@/datatables';
import {
  createPersistedStore,
  refreshableSettings,
  TableSettingsWithRefreshable,
} from '@@/datatables/types';
import { useTableState } from '@@/datatables/useTableState';
import { Widget } from '@@/Widget';
import { TableSettingsMenuAutoRefresh } from '@@/datatables/TableSettingsMenuAutoRefresh';
import { IconProps } from '@@/Icon';

import { columns } from './columns';
import { useResourceRows } from './useResourceRows';

const storageKey = 'helm-resources';

export function createStore(storageKey: string) {
  return createPersistedStore<TableSettingsWithRefreshable>(
    storageKey,
    'name',
    (set) => ({
      ...refreshableSettings(set),
    })
  );
}

const settingsStore = createStore('helm-resources');

type Props = {
  environmentId?: EnvironmentId;
  name?: string;
  namespace?: string;
  revision?: number;
  /** When provided, the table renders these resources instead of fetching the
   * release's — used to show namespace resources when no release exists.
   */
  resources?: GenericResource[];
  isLoading?: boolean;
  /** set false to suppress the global error toast when the release can't be
   * fetched (the add-on view renders its own error state instead). */
  withErrorNotification?: boolean;
  /** When standalone, the table renders as its own widget.
   */
  standalone?: boolean;
  title?: ReactNode;
  titleIcon?: IconProps['icon'];
  description?: ReactNode;
};

export function ResourcesTable({
  environmentId: environmentIdProp,
  name: nameProp,
  namespace: namespaceProp,
  revision: revisionProp,
  resources,
  isLoading = false,
  standalone = false,
  title,
  titleIcon,
  description,
  withErrorNotification = true,
}: Props = {}) {
  const routeEnvironmentId = useEnvironmentId(false);
  const { params } = useCurrentStateAndParams();
  const environmentId = environmentIdProp ?? routeEnvironmentId;
  const name = nameProp ?? params.name;
  const namespace = namespaceProp ?? params.namespace;
  const revisionNumber =
    revisionProp ??
    (params.revision ? parseInt(params.revision, 10) : undefined);

  const tableState = useTableState(settingsStore, storageKey);
  const helmReleaseQuery = useHelmRelease(environmentId, name, namespace, {
    showResources: true,
    refetchInterval: tableState.autoRefreshRateMS,
    revision: revisionNumber,
    withErrorNotification,
    // Skip the release fetch when the caller supplies its own resources.
    enabled: resources === undefined,
  });
  const rows = useResourceRows(
    resources ?? helmReleaseQuery.data?.info?.resources,
    environmentId
  );

  const table = (
    <Datatable
      // Standalone uses the datatable's own widget; otherwise no widget, to
      // avoid extra padding from the internal TableContainer when the caller
      // wraps it.
      noWidget={!standalone}
      // A disabled query still reports isLoading, so ignore it when the caller
      // supplied resources and reflects its own loading via the isLoading prop.
      isLoading={
        isLoading || (resources === undefined && helmReleaseQuery.isLoading)
      }
      dataset={rows}
      columns={columns}
      includeSearch
      settingsManager={tableState}
      emptyContentLabel="No resources found"
      title={title}
      titleIcon={titleIcon}
      description={description}
      disableSelect
      getRowId={(row) => row.id}
      data-cy="helm-resources-datatable"
      renderTableSettings={() => (
        <TableSettingsMenu>
          <TableSettingsMenuAutoRefresh
            value={tableState.autoRefreshRateMS}
            onChange={(value) => tableState.setAutoRefreshRate(value)}
          />
        </TableSettingsMenu>
      )}
    />
  );

  return standalone ? table : <Widget>{table}</Widget>;
}
